import type { ReactNode } from "react";
import { useState } from "react";
import type { TypographyProps } from "@mui/material";
import { Box, Button, Link as MuiLink, Stack, Typography } from "@mui/material";
import TextField from "@mui/material/TextField";
import { useTranslation } from "@privacy/translations";
import { Controller, useForm } from "react-hook-form";
import { Link } from "react-router";
import { WhiteSpace } from "@privacy/ui";
import { Drawer } from "../../shared/Drawer";
import {
  addCommentToDpiaPreAssessmentMutationOptions,
  completeDpiaPreAssessmentMutationOptions,
  dpiaByIdQueryOptions,
  dpiaPreAssessmentFormQueryOptions,
  updateDpiaPreAssessmentAnswersMutationOptions,
} from "../../api/dpia";
import type { QuestionCard } from "../../dynamicForm";
import { DynamicForm } from "../../dynamicForm";
import { QuestionCardContent } from "../../dynamicForm/multiStepForm/QuestionCardContent";
import { TraverseQuestionCardComponentBasedOnRule } from "../../dynamicForm/shared/TraverseQuestionCard";
import { TriangleAlertIcon } from "../../shared/icons";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import type { DpiaById } from "../../api/dpia.types";
import { ErrorMessage } from "../../dynamicForm/inlineForm/ErrorMessage";
import * as FormDialog from "../../shared/FormDialog";
import { PAGE_PADDING_X } from "../../shared/Page";
import { TextFieldActionButtons } from "../../shared/TextFieldActionButtons";
import { DpiaDrawerHeader } from "./DpiaDrawerHeader";
import { ArrowSectionButton } from "../../dynamicForm/multiStepForm/Sections";
import { DynamicGrid } from "../../dynamicGrid";
import { useMutation, useQuery } from "@tanstack/react-query";

export const DpiaPreAssessmentForm = () => {
  const dpiaId = DynamicGrid.useSelectedRowId();
  const dpiaQuery = useQuery(dpiaByIdQueryOptions(dpiaId));
  const updatePreAssessmentAnswersMutation = useMutation(updateDpiaPreAssessmentAnswersMutationOptions());
  return (
    <DynamicForm.MultiStepFormDialog>
      {(() => {
        if (!dpiaQuery.data) return <Drawer.Loading />;
        const dpia = dpiaQuery.data;

        return (
          <DynamicForm.Provider
            onSave={answer => updatePreAssessmentAnswersMutation.mutateAsync({ id: dpia.id, answers: [answer] })}
            answers={dpia.preAssessment.answers}
            isSaveError={updatePreAssessmentAnswersMutation.isError}
            isSaving={updatePreAssessmentAnswersMutation.isPending}
          >
            <DpiaDrawerHeader dpia={dpia} activeTab="preAssessment" />
            {dpia.preAssessment.isCompleted ? (
              <PreAssessmentResult dpia={dpia} completedPreAssessment={dpia.preAssessment} />
            ) : (
              <PreAssesmentForm dpiaId={dpia.id} />
            )}
          </DynamicForm.Provider>
        );
      })()}
    </DynamicForm.MultiStepFormDialog>
  );
};

const PageTitle = ({ children, sx }: { children: ReactNode; sx?: TypographyProps["sx"] }) => {
  return (
    <Typography variant="h1" sx={{ fontWeight: "600", ...sx }}>
      {children}
    </Typography>
  );
};

const PageSubtitle = ({ children }: { children: ReactNode }) => {
  return (
    <Typography sx={{ color: theme => theme.palette.text.tertiary, fontSize: theme => theme.typography.pxToRem(16) }}>
      {children}
    </Typography>
  );
};

const PreAssesmentForm = ({ dpiaId }: { dpiaId: string }) => {
  const { t } = useTranslation();
  const dpiaPreAssessmentFormQuery = useQuery(dpiaPreAssessmentFormQueryOptions());
  const completePreAssessmentMutation = useMutation(completeDpiaPreAssessmentMutationOptions());

  if (!dpiaPreAssessmentFormQuery.data) return <Drawer.Loading />;

  const form = dpiaPreAssessmentFormQuery.data;
  const firstSection = form.sections[0];

  return (
    <Stack sx={{ px: PAGE_PADDING_X, mx: "auto", maxWidth: 900, mt: 8, mb: 4 }}>
      <Stack sx={{ gap: 2 }}>
        <PageTitle>{t("DpiaPreAssessment.Header")}</PageTitle>
        <PageSubtitle>{t("DpiaPreAssessment.Body")}</PageSubtitle>
        <Box>
          <MuiLink
            sx={{ fontSize: theme => theme.typography.pxToRem(14) }}
            component={"button"}
            onClick={() => completePreAssessmentMutation.mutate({ id: dpiaId, skip: true })}
          >
            {t("DpiaPreAssessment.Skip")}
          </MuiLink>
        </Box>
      </Stack>
      <Stack sx={{ py: 5 }}>
        {firstSection.questionCards.map((rootQuestionCard: QuestionCard) => (
          <TraverseQuestionCardComponentBasedOnRule
            key={rootQuestionCard.id}
            rootQuestionCard={rootQuestionCard}
            render={questionCard => (
              <QuestionCardContent questionCard={questionCard}>
                <DynamicForm.Input variant="multiStep" questionCard={questionCard} />
              </QuestionCardContent>
            )}
          />
        ))}

        <Stack sx={{ alignItems: "center", gap: 2 }}>
          {completePreAssessmentMutation.error && <ErrorMessage />}
          <Button
            size="large"
            loading={completePreAssessmentMutation.isPending}
            onClick={() => completePreAssessmentMutation.mutate({ id: dpiaId, skip: false })}
          >
            {t("DpiaPreAssessment.ViewResult")}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

const PreAssessmentResult = ({
  dpia,
  completedPreAssessment,
}: {
  dpia: DpiaById;
  completedPreAssessment: Extract<DpiaById["preAssessment"], { isCompleted: true }>;
}) => {
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const [isEditingComment, setIsEditingComment] = useState(false);

  const addCommentToDpiaPreAssessmentMutation = useMutation(addCommentToDpiaPreAssessmentMutationOptions());
  const {
    control,
    formState: { isDirty },
    resetField,
  } = useForm<{ comment: string }>({
    defaultValues: { comment: completedPreAssessment.comment ?? "" },
  });

  const setCommentToUneditedState = (updatedComment?: string) => {
    resetField("comment", updatedComment ? { defaultValue: updatedComment } : undefined);
    setIsEditingComment(false);
    addCommentToDpiaPreAssessmentMutation.reset();
  };

  return (
    <Stack sx={{ px: PAGE_PADDING_X, mx: "auto", maxWidth: 1200, mt: 8, mb: 4 }}>
      <Stack sx={{ flexDirection: "row", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <Stack sx={{ gap: 2, minWidth: 300, maxWidth: 680 }}>
          <Typography
            sx={{
              fontSize: theme => theme.typography.pxToRem(12),
              textTransform: "uppercase",
              fontWeight: "700",
              letterSpacing: "0.5px",
              color: theme => theme.palette.primary.main,
            }}
          >
            {t(`DpiaPreAssessment.Result.Header.Title`)}
          </Typography>

          {(() => {
            if (completedPreAssessment.completionType === "skipped") {
              return (
                <>
                  <PageTitle>{t(`DpiaPreAssessment.Result.Header.Skipped`)}</PageTitle>
                  <PageSubtitle>{t(`DpiaPreAssessment.Result.Body.Skipped`)}</PageSubtitle>
                </>
              );
            }

            if (completedPreAssessment.dpiaRecommended) {
              return (
                <>
                  <PageTitle>{t(`DpiaPreAssessment.Result.Header.DpiaNeeded`)}</PageTitle>
                  <PageSubtitle>{t(`DpiaPreAssessment.Result.Body.DpiaNeeded`)}</PageSubtitle>
                  <CompletedPreAssessmentInfo completedPreAssessment={completedPreAssessment} />
                </>
              );
            }

            if (!completedPreAssessment.dpiaRecommended) {
              return (
                <>
                  <PageTitle>{t(`DpiaPreAssessment.Result.Header.DpiaNotNeeded`)}</PageTitle>
                  <PageSubtitle>{t(`DpiaPreAssessment.Result.Body.DpiaNotNeeded`)}</PageSubtitle>
                  <MuiLink
                    component={Link}
                    to={paths.dpiaForm(dpia.id)}
                    sx={{ fontSize: theme => theme.typography.pxToRem(14) }}
                    onClick={() => navigate(paths.dpiaForm(dpia.id))}
                  >
                    {t("DpiaPreAssessment.StartImpactAssessmentAnyways")}
                  </MuiLink>
                  <CompletedPreAssessmentInfo completedPreAssessment={completedPreAssessment} />
                </>
              );
            }
          })()}
        </Stack>

        <Controller
          control={control}
          name="comment"
          render={({ field: { ref, ...field } }) => (
            <FormDialog.Field
              onBlur={TextFieldActionButtons.onTextFieldWrapperBlur({
                isDirty,
                onCancel: () => setCommentToUneditedState(),
              })}
              errorMessage={addCommentToDpiaPreAssessmentMutation.isError && t("DynamicForm.Inline.FailedToSave")}
            >
              <FormDialog.Label htmlFor="comment">{t("DpiaPreAssessment.Comment.Label")}</FormDialog.Label>
              <TextField
                {...field}
                size="small"
                multiline
                onFocus={() => dpia.canEdit && setIsEditingComment(true)}
                sx={{ width: ["100%", "400px"], ...TextFieldActionButtons.TextFieldSx }}
                rows={8}
                fullWidth
                placeholder={t("DpiaPreAssessment.Comment.Placeholder")}
                error={Boolean(addCommentToDpiaPreAssessmentMutation.error)}
                id="comment"
                inputRef={ref}
                slotProps={{
                  input: {
                    endAdornment: (
                      <TextFieldActionButtons
                        isEditing={isEditingComment}
                        isSaving={addCommentToDpiaPreAssessmentMutation.isPending}
                        onSave={() =>
                          addCommentToDpiaPreAssessmentMutation.mutate(
                            { dpiaId: dpia.id, payload: { comment: field.value } },
                            { onSuccess: () => setCommentToUneditedState(field.value) }
                          )
                        }
                        isDirty={isDirty}
                        onCancel={() => setCommentToUneditedState()}
                      />
                    ),
                  },
                }}
              />
            </FormDialog.Field>
          )}
        />
      </Stack>
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 10 }}>
        <Box />
        <ArrowSectionButton
          direction="right"
          sectionName={t("DpiaPreAssessment.StartImpactAssessment")}
          onClick={() => navigate(paths.dpiaForm(dpia.id))}
        />
      </Box>
    </Stack>
  );
};

const CompletedPreAssessmentInfo = ({
  completedPreAssessment,
}: {
  completedPreAssessment: Extract<DpiaById["preAssessment"], { isCompleted: true }>;
}) => {
  const { t, formatDate } = useTranslation();

  return (
    <Stack sx={{ pt: 2, gap: 1 }}>
      {completedPreAssessment.riskCriteria.length > 0 && (
        <Typography sx={{ fontSize: theme => theme.typography.pxToRem(16), fontWeight: 600 }}>
          {t("RegistrationDrawer.Dpia.RiskCriteria.Title")}
        </Typography>
      )}
      {completedPreAssessment.riskCriteria.map(riskCriteria => (
        <Stack
          key={riskCriteria}
          direction="row"
          sx={{ alignItems: "center", gap: 1, color: theme => theme.palette.text.tertiary }}
        >
          <TriangleAlertIcon size={14} color={theme => theme.palette.brandOrange["500"]} />
          <Typography sx={{ color: "inherit", fontSize: theme => theme.typography.pxToRem(14) }}>
            {riskCriteria}
          </Typography>
        </Stack>
      ))}
      <Typography
        sx={{ pt: 2, fontSize: theme => theme.typography.pxToRem(12), color: theme => theme.palette.text.tertiary }}
      >
        {(() => {
          if (completedPreAssessment.completionType === "manual" && completedPreAssessment.completedBy) {
            return (
              <>
                {t("DpiaPreAssessment.Manual.CompletedBy")}
                <WhiteSpace />
                <span style={{ fontWeight: "bold" }}>
                  {completedPreAssessment.completedBy.firstName} {completedPreAssessment.completedBy.lastName}
                </span>
                <WhiteSpace />
                {completedPreAssessment.completedAt ? formatDate(completedPreAssessment.completedAt, {}) : ""}
              </>
            );
          }

          if (completedPreAssessment.completionType === "auto") {
            return (
              <>
                {t("DpiaPreAssessment.Auto.Connected.Registration")}
                <span style={{ fontWeight: "bold" }}>{completedPreAssessment.registrationName}</span>.
              </>
            );
          }

          return null;
        })()}
      </Typography>
    </Stack>
  );
};
